import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Typography,
  Alert,
  Card,
  CardContent,
  Box,
  Button,
  TextField,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Search from "@mui/icons-material/Search";
import ChartCard from "@/components/data-visuals/ChartCard";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { DOCLINK_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import {
  normalizeAppointmentOverview,
  normalizeConnectedChemistsResponse,
} from "@/utils/hubMedApiNormalize";
import HubMedAdminFilters from "@/components/admin/hubmed/HubMedAdminFilters";
import HubMedAdminSummaryCards from "@/components/admin/hubmed/HubMedAdminSummaryCards";
import HubMedAppointmentsChart from "@/components/admin/hubmed/HubMedAppointmentsChart";
import HubMedConnectedChemistsTable from "@/components/admin/hubmed/HubMedConnectedChemistsTable";
import HubMedDailyLogDialog from "@/components/admin/hubmed/HubMedDailyLogDialog";
import { mapConsultTypeBreakdownToPie } from "@/components/admin/hubmed/hubMedChartHelpers";

const defaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return {
    dateStart: start.toISOString().split("T")[0],
    dateEnd: end.toISOString().split("T")[0],
  };
};

const CHEMIST_FILTER_DEBOUNCE_MS = 400;

const HubMedAdminOverviewPage = () => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const initial = useMemo(() => defaultDateRange(), []);
  const [dateStart, setDateStart] = useState(initial.dateStart);
  const [dateEnd, setDateEnd] = useState(initial.dateEnd);
  const [nameFilter, setNameFilter] = useState("");
  const [debouncedChemistFilter, setDebouncedChemistFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [connected, setConnected] = useState(null);
  const [error, setError] = useState(null);
  const [timeChartOpen, setTimeChartOpen] = useState(false);
  const [dailyLogOpen, setDailyLogOpen] = useState(false);

  const onDateRangeChange = useCallback((start, end) => {
    setDateStart(start || "");
    setDateEnd(end || "");
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedChemistFilter(nameFilter.trim());
    }, CHEMIST_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [nameFilter]);

  useEffect(() => {
    if (!dateStart || !dateEnd) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const params = { dateStart, dateEnd };
        if (debouncedChemistFilter) params.chemistName = debouncedChemistFilter;
        const [ovRaw, ccRaw] = await Promise.all([
          phappsRequest(
            API_PATHS.HUBMED_ADMIN_APPOINTMENT_OVERVIEW,
            "GET",
            null,
            params
          ),
          phappsRequest(
            API_PATHS.HUBMED_ADMIN_CONNECTED_CHEMISTS,
            "GET",
            null,
            params
          ),
        ]);
        if (!cancelled) {
          setOverview(normalizeAppointmentOverview(ovRaw));
          setConnected(normalizeConnectedChemistsResponse(ccRaw));
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setOverview(null);
          setConnected(null);
          setError(
            getAdminApiErrorMessage(err, "Failed to load HubMed overview")
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dateStart, dateEnd, debouncedChemistFilter]);

  const summary = overview?.summary ?? {};

  const totalAppointmentsDisplay = useMemo(() => {
    const raw = summary.totalAppointmentsInRange;
    if (raw == null || raw === "") return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }, [summary.totalAppointmentsInRange]);

  const avgWeek = summary.averageAppointmentsPerWeek;

  const activeChemistsCount = useMemo(() => {
    const raw = summary.totalActiveChemists;
    if (raw == null || raw === "") return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }, [summary.totalActiveChemists]);

  const pieSlices = useMemo(
    () => mapConsultTypeBreakdownToPie(overview?.consultTypeBreakdownRange),
    [overview]
  );

  const chemistRows = useMemo(
    () => (Array.isArray(connected?.chemists) ? connected.chemists : []),
    [connected]
  );

  const statsLoading = loading && !overview;

  return (
    <div className="h-full min-h-0 flex flex-col bg-white overflow-x-hidden">
      <div className="flex-1 min-h-0 flex flex-col p-4 gap-3 w-full max-w-full">
        {loading && !overview ? (
          <div className="flex flex-1 items-center justify-center min-h-[280px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading HubMed overview…</p>
            </div>
          </div>
        ) : (
          <>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ color: DOCLINK_COLORS.DARK_PURPLE, flexShrink: 0 }}
            >
              HubMed Admin overview
            </Typography>

            {error ? (
              <Alert severity="warning" sx={{ flexShrink: 0 }}>
                {error}
              </Alert>
            ) : null}

            <Box
              component="section"
              className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-stretch"
              sx={{ flexShrink: 0 }}
            >
              <div className="min-w-0 h-full flex">
                <HubMedAdminFilters
                  dateStart={dateStart}
                  dateEnd={dateEnd}
                  onDateRangeChange={onDateRangeChange}
                />
              </div>
              <HubMedAdminSummaryCards
                loading={statsLoading}
                totalAppointmentsInRange={totalAppointmentsDisplay}
                averageAppointmentsPerWeek={avgWeek}
                activeChemistsCount={activeChemistsCount}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: 2,
                alignItems: "stretch",
                flex: { xs: "none", lg: 1 },
                minHeight: { xs: 0, lg: 0 },
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: { xs: 0, lg: 0 },
                  borderColor: DOCLINK_COLORS.DARK_PURPLE,
                  borderRadius: 2,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent
                  sx={{
                    p: 0,
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: 2,
                      py: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      fontWeight={700}
                      sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}
                    >
                      Chemists
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setTimeChartOpen(true)}
                        disabled={!overview}
                        sx={{
                          textTransform: "none",
                          borderColor: DOCLINK_COLORS.DARK_PURPLE,
                          color: DOCLINK_COLORS.DARK_PURPLE,
                          fontWeight: 600,
                        }}
                      >
                        See appointments over time
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setDailyLogOpen(true)}
                        sx={{
                          textTransform: "none",
                          borderColor: DOCLINK_COLORS.DARK_PURPLE,
                          color: DOCLINK_COLORS.DARK_PURPLE,
                          fontWeight: 600,
                        }}
                      >
                        View Daily Log
                      </Button>
                      <TextField
                        size="small"
                        placeholder="Filter by chemist name"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 260 }}
                      />
                    </Box>
                  </Box>
                  <Divider />
                  <HubMedConnectedChemistsTable
                    embedded
                    fillHeight={isLgUp}
                    chemists={chemistRows}
                    loading={loading}
                    visible
                    useDocLinkGridStyle
                    showConsultBreakdown={false}
                  />
                </CardContent>
              </Card>

              <Box
                sx={{
                  width: { xs: "100%", lg: 420 },
                  flex: { xs: "none", lg: "0 0 auto" },
                  display: "flex",
                  flexDirection: "column",
                  minHeight: { xs: 360, lg: 0 },
                  alignSelf: { lg: "stretch" },
                }}
              >
                {pieSlices.length ? (
                  <Box
                    sx={{
                      flex: isLgUp ? 1 : "none",
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                  <ChartCard
                    title="Appointments by consult type"
                    hideMainChart
                    fillParentHeight={isLgUp}
                    pieChartMinHeight={isLgUp ? 320 : 400}
                    data={{}}
                    primaryXAxis={{}}
                    primaryYAxis={{}}
                    borderClass="border-4 border-purple-light"
                    series={[]}
                    pieData={pieSlices}
                    pieTitle="By consult type"
                    pieStartAngle="0"
                    pieEndAngle="360"
                    pieInnerRadius="54%"
                    pieLegendSettings={{
                      enablePages: false,
                      textStyle: { fontWeight: "500", size: "11px" },
                    }}
                  />
                  </Box>
                ) : (
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: DOCLINK_COLORS.DARK_PURPLE,
                      borderRadius: 2,
                      flex: isLgUp ? 1 : "none",
                      minHeight: isLgUp ? 0 : 400,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 3,
                    }}
                  >
                    <Typography color="text.secondary" textAlign="center">
                      No consult type totals for this range.
                    </Typography>
                  </Card>
                )}
              </Box>
            </Box>
          </Box>

            {dailyLogOpen ? (
              <HubMedDailyLogDialog onClose={() => setDailyLogOpen(false)} />
            ) : null}

            <Dialog
              open={timeChartOpen}
              onClose={() => setTimeChartOpen(false)}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle
                sx={{ color: DOCLINK_COLORS.DARK_PURPLE, fontWeight: 700 }}
              >
                HubMed appointments over time
              </DialogTitle>
              <DialogContent>
                <Box sx={{ "& > div": { mt: 0 }, pt: 1 }}>
                  <HubMedAppointmentsChart
                    appointmentsPerDay={overview?.appointmentsPerDay}
                    loading={loading && !overview}
                    visible
                    mainChartMinHeight={360}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setTimeChartOpen(false)} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
};

export default HubMedAdminOverviewPage;
